import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CrudPage from "@/components/CrudPage";
import { PageHeader, StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, RotateCcw, ClipboardList } from "lucide-react";
import { toast } from "sonner";

function ClientHomework() {
  const [items, setItems] = useState([]);
  const load = async () => { const r = await api.get("/homework"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/homework/${id}/status`, { status }); toast.success("Updated"); await load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div>
      <PageHeader title="Homework" subtitle="Homework assigned to your dog by your trainer." />
      <div className="space-y-3">
        {items.map((h) => (
          <Card key={h.id} className="border-border" data-testid="client-homework-item">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <ClipboardList size={16} className="text-primary" strokeWidth={1.5} />
                    <span className="font-medium">{h.title}</span>
                    <StatusBadge value={h.status} />
                  </div>
                  <p className="text-sm text-muted-foreground mt-1.5">{h.instructions}</p>
                  <div className="text-xs text-muted-foreground mt-2">{h.dog_name} · {h.trainer_name} · Due {fmtDate(h.due_date)}</div>
                </div>
                {h.status === "assigned" ? (
                  <Button size="sm" className="gap-1.5" onClick={() => setStatus(h.id, "completed")} data-testid="hw-mark-complete"><CheckCircle2 size={15} /> Mark done</Button>
                ) : h.status === "completed" ? (
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setStatus(h.id, "assigned")} data-testid="hw-reopen"><RotateCcw size={15} /> Undo</Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-muted-foreground text-center py-12">No homework yet.</p>}
      </div>
    </div>
  );
}

export default function Homework() {
  const { user } = useAuth();
  if (user?.role === "client") return <ClientHomework />;

  return (
    <CrudPage
      title="Homework"
      subtitle="Assign and track homework for dogs after classes."
      endpoint="homework"
      testid="homework"
      columns={[
        { key: "title", label: "Homework", render: (i) => <span className="font-medium">{i.title}</span> },
        { key: "dog_name", label: "Dog" },
        { key: "trainer_name", label: "Trainer" },
        { key: "class_name", label: "Class", render: (i) => i.class_name || "—" },
        { key: "due_date", label: "Due", date: true },
        { key: "status", label: "Status", badge: true },
      ]}
      fields={[
        { name: "dog_id", label: "Dog", type: "select", optionsFrom: "dogs", labelKey: "name" },
        { name: "trainer_id", label: "Trainer", type: "select", optionsFrom: "trainers", labelKey: "full_name" },
        { name: "class_id", label: "Class", type: "select", optionsFrom: "classes", labelKey: "name" },
        { name: "title", label: "Homework title" },
        { name: "instructions", label: "Instructions", type: "textarea" },
        { name: "video_url", label: "Video / link (optional)" },
        { name: "due_date", label: "Due date", type: "date" },
        { name: "status", label: "Status", type: "select", default: "assigned", options: [
          { value: "assigned", label: "Assigned" }, { value: "completed", label: "Completed" }, { value: "reviewed", label: "Reviewed" },
        ] },
      ]}
    />
  );
}
